import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TablePagination,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { Videocam, Audiotrack, CalendarToday, AccessTime } from '@mui/icons-material';
import LoadingBackdrop from "../Common/LoadingBackdrop";
import { PER_PAGE_OPTIONS } from "../../constants/pagination";

const FeedbacksTable = ({
  data = [],
  searchText = "",
  pagination = { total: 0, page: 1, per_page: 10, total_pages: 0 },
  onPageChange,
  onRowsPerPageChange
}) => {
  const loading = useSelector((state) => state.feedbacks.isLoading);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsFeedback, setDetailsFeedback] = useState(null);

  const openDetails = (feedback) => {
    setDetailsFeedback(feedback);
    setDetailsOpen(true);
  };

  const closeDetails = () => {
    setDetailsOpen(false);
    setDetailsFeedback(null);
  };

  if (loading) {
    return <LoadingBackdrop open={loading} />;
  }

  // Ensure data is an array
  const feedbacks = Array.isArray(data) ? data : [];

  return (
    <Box sx={{ py: 2 }}>
      <TableContainer
        component={Paper}
        sx={{
          marginTop: 3,
          boxShadow: 2,
          borderRadius: 1,
          overflow: 'hidden'
        }}
      >
        <Table size="medium">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ py: 2, px: 2 }}><strong>Message</strong></TableCell>
              <TableCell sx={{ width: 220, py: 2, px: 2 }}><strong>User</strong></TableCell>
              <TableCell sx={{ width: 260, py: 2, px: 2 }}><strong>Session</strong></TableCell>
              <TableCell sx={{ width: 160, py: 2, px: 2 }}><strong>Parent Type</strong></TableCell>
              <TableCell sx={{ width: 220, py: 2, px: 2 }}><strong>Parent Name</strong></TableCell>
              <TableCell sx={{ width: 180, py: 2, px: 2 }}><strong>Date & Time</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {feedbacks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="subtitle1" color="text.secondary">
                    {searchText ? "No feedbacks found matching your search" : "No feedbacks available"}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              feedbacks.map((feedback) => {
                const dt = new Date(feedback.timestamp * 1000);

                return (
                  <React.Fragment key={feedback.id}>
                    <TableRow>
                      <TableCell sx={{ py: 2, px: 2 }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                          {feedback.feedback_message}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: 220, py: 2, px: 2 }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {feedback.user_full_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {feedback.user_email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ width: 260, py: 2, px: 2 }}>
                        <Box display="flex" alignItems="center" gap={1} sx={{ cursor: 'pointer' }} onClick={() => openDetails(feedback)}>
                          {feedback.is_quick_tap ? <Videocam fontSize="small" /> : <Audiotrack fontSize="small" />}
                          <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word', textDecoration: 'underline', textUnderlineOffset: '2px' }}>
                            {feedback.session_name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ width: 160, py: 2, px: 2 }}>
                        <Typography variant="body2">{feedback.parent_type || '—'}</Typography>
                      </TableCell>
                      <TableCell sx={{ width: 220, py: 2, px: 2 }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{feedback.parent_name || '—'}</Typography>
                      </TableCell>
                      <TableCell sx={{ width: 180, py: 2, px: 2 }}>
                        <Box>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <CalendarToday sx={{ fontSize: '0.875rem', color: 'text.secondary' }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {dt.toLocaleDateString()}
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <AccessTime sx={{ fontSize: '0.75rem', color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              {dt.toLocaleTimeString()}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page - 1}
          onPageChange={onPageChange}
          rowsPerPage={pagination.per_page}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={PER_PAGE_OPTIONS}
          labelRowsPerPage="Page Size:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`}
          showFirstButton
          showLastButton
        />
      </TableContainer>
      <Dialog open={detailsOpen} onClose={closeDetails} maxWidth="sm" fullWidth>
        <DialogTitle>Session Details</DialogTitle>
        <DialogContent dividers>
          {detailsFeedback && (
            <Box display="grid" gap={1}>
              <Box>
                <Typography variant="caption" color="text.secondary">Session Name</Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  {detailsFeedback.is_quick_tap ? <Videocam fontSize="small" /> : <Audiotrack fontSize="small" />}
                  <Typography variant="body1">{detailsFeedback.session_name}</Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Session ID</Typography>
                <Typography variant="body1">{detailsFeedback.session_id || '—'}</Typography>
              </Box>
              <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Parent Type</Typography>
                  <Typography variant="body1">{detailsFeedback.parent_type || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Parent Name</Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{detailsFeedback.parent_name || '—'}</Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetails}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeedbacksTable;
